/**
 * UBL 2.1 XML Builder for XRechnung
 * Generates EN 16931 compliant invoice XML
 */

import {XRechnungInvoiceData, XRechnungLineItem, VatBreakdown, SellerParty, BuyerParty, PaymentMeans} from './types';

/**
 * XML namespaces for UBL 2.1 Invoice
 */
const NAMESPACES = {
    ubl: 'urn:oasis:names:specification:ubl:schema:xsd:Invoice-2',
    cac: 'urn:oasis:names:specification:ubl:schema:xsd:CommonAggregateComponents-2',
    cbc: 'urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2',
};

/**
 * Escape XML special characters
 */
function escapeXml(text: string): string {
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;');
}

/**
 * Format date as ISO 8601 (YYYY-MM-DD)
 */
function formatDate(date: Date): string {
    return date.toISOString().split('T')[0];
}

/**
 * Format amount with 2 decimal places
 */
function formatAmount(amount: number): string {
    return amount.toFixed(2);
}

/**
 * Build a simple XML element
 */
function element(tag: string, content: string, attributes?: Record<string, string>): string {
    const attrs = attributes
        ? ' ' + Object.entries(attributes).map(([k, v]) => `${k}="${escapeXml(v)}"`).join(' ')
        : '';
    return `<${tag}${attrs}>${escapeXml(content)}</${tag}>`;
}

/**
 * Build a CBC (CommonBasicComponents) element
 */
function cbc(name: string, content: string, attributes?: Record<string, string>): string {
    return element(`cbc:${name}`, content, attributes);
}

/**
 * Build a CAC (CommonAggregateComponents) wrapper
 */
function cac(name: string, children: string[]): string {
    return `<cac:${name}>\n${children.join('\n')}\n</cac:${name}>`;
}

/**
 * PEPPOL default business process identifier
 */
const PEPPOL_BUSINESS_PROCESS = 'urn:fdc:peppol.eu:2017:poacc:billing:01:1.0';

/**
 * Build document header elements
 */
function buildDocumentHeader(invoice: XRechnungInvoiceData): string[] {
    const elements: string[] = [];

    // Customization ID (BT-24) - XRechnung profile
    elements.push(cbc('CustomizationID', invoice.specificationId));

    // Profile ID (BT-23) - Business process (required for PEPPOL)
    elements.push(cbc('ProfileID', invoice.businessProcessType || PEPPOL_BUSINESS_PROCESS));

    // Invoice Number (BT-1)
    elements.push(cbc('ID', invoice.invoiceNumber));

    // Issue Date (BT-2)
    elements.push(cbc('IssueDate', formatDate(invoice.invoiceDate)));

    // Due Date (BT-9)
    if (invoice.paymentTerms?.dueDate) {
        elements.push(cbc('DueDate', formatDate(invoice.paymentTerms.dueDate)));
    }

    // Invoice Type Code (BT-3)
    elements.push(cbc('InvoiceTypeCode', invoice.invoiceTypeCode));

    // Note (BT-22)
    if (invoice.note) {
        elements.push(cbc('Note', invoice.note));
    }

    // Document Currency Code (BT-5)
    elements.push(cbc('DocumentCurrencyCode', invoice.currencyCode));

    // Buyer Reference (BT-10) - required for XRechnung
    if (invoice.buyerReference) {
        elements.push(cbc('BuyerReference', invoice.buyerReference));
    }

    return elements;
}

/**
 * Build postal address element
 */
function buildPostalAddress(address: SellerParty['postalAddress'] | BuyerParty['postalAddress']): string {
    const children: string[] = [];

    children.push(cbc('StreetName', address.streetName));

    if (address.additionalStreetName) {
        children.push(cbc('AdditionalStreetName', address.additionalStreetName));
    }

    children.push(cbc('CityName', address.cityName));
    children.push(cbc('PostalZone', address.postalZone));

    if (address.countrySubdivision) {
        children.push(cbc('CountrySubentity', address.countrySubdivision));
    }

    children.push(cac('Country', [cbc('IdentificationCode', address.countryCode)]));

    return cac('PostalAddress', children);
}

/**
 * Get non-empty string or undefined
 */
function nonEmpty(value?: string): string | undefined {
    return value && value.trim() ? value.trim() : undefined;
}

/**
 * Build contact element
 * For XRechnung: BT-41 (Name), BT-42 (Telephone), BT-43 (Email) are required for seller
 */
function buildContact(contact: NonNullable<SellerParty['contact']>, fallbackName?: string, fallbackEmail?: string): string {
    const children: string[] = [];

    // BT-41: Contact point - REQUIRED for XRechnung seller (BR-DE-05)
    const contactName = nonEmpty(contact.name) || nonEmpty(fallbackName) || 'Kontakt';
    children.push(cbc('Name', contactName));

    // BT-42: Telephone
    const phone = nonEmpty(contact.telephone);
    if (phone) {
        children.push(cbc('Telephone', phone));
    }

    // BT-43: Email - REQUIRED for XRechnung seller (BR-DE-07)
    const contactEmail = nonEmpty(contact.email) || nonEmpty(fallbackEmail);
    if (contactEmail) {
        children.push(cbc('ElectronicMail', contactEmail));
    }

    return cac('Contact', children);
}

/**
 * Build party tax scheme element
 */
function buildPartyTaxScheme(vatId?: string): string {
    if (!vatId) return '';

    return cac('PartyTaxScheme', [
        cbc('CompanyID', vatId),
        cac('TaxScheme', [cbc('ID', 'VAT')]),
    ]);
}

/**
 * Build party legal entity element
 * BT-30: Seller legal registration identifier (CompanyID)
 */
function buildPartyLegalEntity(name: string, companyId?: string): string {
    const children: string[] = [cbc('RegistrationName', name)];

    if (companyId) {
        // Add CompanyID with schemeID for German commercial register (HRB)
        // schemeID '0204' is for German register identifiers
        children.push(cbc('CompanyID', companyId, {schemeID: '0204'}));
    }

    return cac('PartyLegalEntity', children);
}

/**
 * Build seller party element
 */
function buildAccountingSupplierParty(seller: SellerParty): string {
    const partyChildren: string[] = [];

    // Get non-empty values
    const sellerEmail = nonEmpty(seller.contact?.email);
    const sellerVatId = nonEmpty(seller.identification.vatId);
    const sellerTaxNumber = nonEmpty(seller.identification.taxNumber);
    const sellerCompanyId = nonEmpty(seller.companyId);
    const sellerGln = nonEmpty(seller.identification.gln);
    const sellerName = nonEmpty(seller.name) || 'Verkäufer';

    // Endpoint ID (BT-34) - REQUIRED for PEPPOL (PEPPOL-EN16931-R020)
    // Use GLN (0088), email (EM), or VAT ID as fallback
    if (sellerGln) {
        partyChildren.push(cbc('EndpointID', sellerGln, {schemeID: '0088'}));
    } else if (sellerEmail) {
        partyChildren.push(cbc('EndpointID', sellerEmail, {schemeID: 'EM'}));
    } else if (sellerVatId) {
        partyChildren.push(cbc('EndpointID', sellerVatId, {schemeID: '0190'}));
    } else {
        // Must have EndpointID - use company name as last resort
        partyChildren.push(cbc('EndpointID', sellerName.replace(/\s+/g, '').substring(0, 50), {schemeID: '0190'}));
    }

    // Party Identification (BT-29) - optional, only if GLN is available
    // BR-CO-26 is satisfied by PartyTaxScheme (BT-31) or PartyLegalEntity/CompanyID (BT-30)
    if (sellerGln) {
        partyChildren.push(cac('PartyIdentification', [
            cbc('ID', sellerGln, {schemeID: '0088'}),  // GLN
        ]));
    }

    // Party Name
    partyChildren.push(cac('PartyName', [cbc('Name', sellerName)]));

    // Postal Address
    partyChildren.push(buildPostalAddress(seller.postalAddress));

    // BR-CO-26 requires at least one of: VAT ID (BT-31), Company ID (BT-30), or Party ID (BT-29)
    // BR-DE-16 requires PartyTaxScheme with VAT ID, tax registration, or tax representative
    // BR-CO-09: VAT identifiers MUST have country prefix (e.g., DE123456789)
    if (sellerVatId && /^[A-Z]{2}/.test(sellerVatId)) {
        // BT-31: Valid VAT ID with country prefix - use TaxScheme 'VAT'
        partyChildren.push(buildPartyTaxScheme(sellerVatId));
    } else if (sellerTaxNumber) {
        // BT-32: Tax registration identifier (Steuernummer) - use TaxScheme 'NOT'
        // 'NOT' means "not applicable" for VAT but still provides tax identification
        // This satisfies BR-DE-16 without violating BR-CO-09
        partyChildren.push(cac('PartyTaxScheme', [
            cbc('CompanyID', sellerTaxNumber),
            cac('TaxScheme', [cbc('ID', 'NOT')]),
        ]));
    }
    // If neither VAT ID nor tax number, BR-CO-26 must be satisfied by CompanyID (BT-30) below

    // Party Legal Entity (BT-30) - CompanyID helps with BR-CO-26
    partyChildren.push(buildPartyLegalEntity(
        nonEmpty(seller.registrationName) || sellerName,
        sellerCompanyId
    ));

    // Contact (required for XRechnung - BR-DE-05, BR-DE-07)
    // Pass seller name as fallback for contact name
    partyChildren.push(buildContact(
        seller.contact || {},
        sellerName,    // Fallback contact name
        sellerEmail    // Use same email for contact
    ));

    return cac('AccountingSupplierParty', [cac('Party', partyChildren)]);
}

/**
 * Build buyer party element
 */
function buildAccountingCustomerParty(buyer: BuyerParty): string {
    const partyChildren: string[] = [];

    // Endpoint ID (BT-49) - REQUIRED for PEPPOL
    // Use Leitweg-ID (0204), GLN (0088), or email (EM) as fallback
    if (buyer.identification?.leitwegId) {
        partyChildren.push(cbc('EndpointID', buyer.identification.leitwegId, {schemeID: '0204'}));
    } else if (buyer.identification?.gln) {
        partyChildren.push(cbc('EndpointID', buyer.identification.gln, {schemeID: '0088'}));
    } else if (buyer.contact?.email) {
        partyChildren.push(cbc('EndpointID', buyer.contact.email, {schemeID: 'EM'}));
    } else {
        // Use buyer name as NA (not applicable) endpoint if no other identifier
        partyChildren.push(cbc('EndpointID', 'NA', {schemeID: '0190'}));
    }

    // Party Identification
    if (buyer.identification?.vatId) {
        partyChildren.push(cac('PartyIdentification', [
            cbc('ID', buyer.identification.vatId, {schemeID: 'VAT'}),
        ]));
    }

    // Party Name
    partyChildren.push(cac('PartyName', [cbc('Name', buyer.name)]));

    // Postal Address
    partyChildren.push(buildPostalAddress(buyer.postalAddress));

    // Party Tax Scheme
    if (buyer.identification?.vatId) {
        const taxScheme = buildPartyTaxScheme(buyer.identification.vatId);
        if (taxScheme) {
            partyChildren.push(taxScheme);
        }
    }

    // Party Legal Entity
    partyChildren.push(buildPartyLegalEntity(buyer.name));

    // Contact (optional for buyer)
    if (buyer.contact) {
        partyChildren.push(buildContact(buyer.contact, buyer.name));
    }

    return cac('AccountingCustomerParty', [cac('Party', partyChildren)]);
}

/**
 * Delivery address type for XRechnung
 */
interface DeliveryAddress {
    streetName?: string;
    cityName?: string;
    postalZone?: string;
    countryCode?: string;
}

/**
 * Build delivery element
 */
function buildDelivery(
    deliveryDate?: Date,
    deliveryLocation?: string,
    deliveryAddress?: DeliveryAddress
): string {
    if (!deliveryDate && !deliveryLocation && !deliveryAddress) return '';

    const children: string[] = [];

    // Actual delivery date (BT-72)
    if (deliveryDate) {
        children.push(cbc('ActualDeliveryDate', formatDate(deliveryDate)));
    }

    // Delivery location (BG-13) with full address for XRechnung
    const addressChildren: string[] = [];

    // Street name (BT-75)
    if (deliveryAddress?.streetName || deliveryLocation) {
        addressChildren.push(cbc('StreetName', deliveryAddress?.streetName || deliveryLocation || ''));
    }

    // City name (BT-77) - required for XRechnung
    if (deliveryAddress?.cityName) {
        addressChildren.push(cbc('CityName', deliveryAddress.cityName));
    }

    // Postal zone (BT-78) - required for XRechnung
    if (deliveryAddress?.postalZone) {
        addressChildren.push(cbc('PostalZone', deliveryAddress.postalZone));
    }

    // Country (BT-80)
    addressChildren.push(cac('Country', [
        cbc('IdentificationCode', deliveryAddress?.countryCode || 'DE'),
    ]));

    if (addressChildren.length > 0) {
        children.push(cac('DeliveryLocation', [
            cac('Address', addressChildren),
        ]));
    }

    return cac('Delivery', children);
}

/**
 * Build payment means element
 */
function buildPaymentMeans(payment: PaymentMeans, paymentReference?: string): string {
    const children: string[] = [];

    // Payment Means Code (BT-81)
    children.push(cbc('PaymentMeansCode', payment.paymentMeansCode));

    // Payment ID / Reference (BT-83)
    if (paymentReference || payment.paymentReference) {
        children.push(cbc('PaymentID', paymentReference || payment.paymentReference!));
    }

    // Payee Financial Account
    const accountChildren: string[] = [
        cbc('ID', payment.iban),
    ];

    if (payment.accountName) {
        accountChildren.push(cbc('Name', payment.accountName));
    }

    if (payment.bic) {
        accountChildren.push(cac('FinancialInstitutionBranch', [
            cbc('ID', payment.bic),
        ]));
    }

    children.push(cac('PayeeFinancialAccount', accountChildren));

    return cac('PaymentMeans', children);
}

/**
 * Build payment terms element
 */
function buildPaymentTerms(note?: string): string {
    if (!note) return '';

    return cac('PaymentTerms', [cbc('Note', note)]);
}

/**
 * Round to 2 decimal places to avoid floating point precision issues
 */
function round2(value: number): number {
    return Math.round(value * 100) / 100;
}

/**
 * Build tax total element
 */
function buildTaxTotal(vatBreakdown: VatBreakdown[], currencyCode: string): string {
    // Round the sum to ensure BR-CO-15 compliance (BT-112 = BT-109 + BT-110)
    const totalTax = round2(vatBreakdown.reduce((sum, vat) => sum + vat.taxAmount, 0));

    const taxSubtotals = vatBreakdown.map(vat => cac('TaxSubtotal', [
        cbc('TaxableAmount', formatAmount(vat.taxableAmount), {currencyID: currencyCode}),
        cbc('TaxAmount', formatAmount(vat.taxAmount), {currencyID: currencyCode}),
        cac('TaxCategory', [
            cbc('ID', vat.categoryCode),
            cbc('Percent', formatAmount(vat.rate)),
            cac('TaxScheme', [cbc('ID', 'VAT')]),
        ]),
    ]));

    return cac('TaxTotal', [
        cbc('TaxAmount', formatAmount(totalTax), {currencyID: currencyCode}),
        ...taxSubtotals,
    ]);
}

/**
 * Build legal monetary total element
 */
function buildLegalMonetaryTotal(invoice: XRechnungInvoiceData): string {
    return cac('LegalMonetaryTotal', [
        cbc('LineExtensionAmount', formatAmount(invoice.lineExtensionAmount), {currencyID: invoice.currencyCode}),
        cbc('TaxExclusiveAmount', formatAmount(invoice.taxExclusiveAmount), {currencyID: invoice.currencyCode}),
        cbc('TaxInclusiveAmount', formatAmount(invoice.taxInclusiveAmount), {currencyID: invoice.currencyCode}),
        cbc('PayableAmount', formatAmount(invoice.payableAmount), {currencyID: invoice.currencyCode}),
    ]);
}

/**
 * Build invoice line element
 */
function buildInvoiceLine(item: XRechnungLineItem, currencyCode: string): string {
    const children: string[] = [];

    // Line ID (BT-126)
    children.push(cbc('ID', item.id));

    // Note (BT-127)
    if (item.note) {
        children.push(cbc('Note', item.note));
    }

    // Invoiced Quantity (BT-129, BT-130)
    children.push(cbc('InvoicedQuantity', formatAmount(item.quantity), {unitCode: item.unitCode}));

    // Line Extension Amount (BT-131)
    children.push(cbc('LineExtensionAmount', formatAmount(item.lineNetAmount), {currencyID: currencyCode}));

    // Item - UBL 2.1 element order: Description, Name, SellersItemIdentification, ClassifiedTaxCategory
    const itemChildren: string[] = [];

    // Description is optional (BT-154) - comes BEFORE Name in UBL 2.1
    if (item.description) {
        itemChildren.push(cbc('Description', item.description));
    }

    // Name is required (BT-153)
    itemChildren.push(cbc('Name', item.name));

    // SellersItemIdentification
    if (item.sellerItemId) {
        itemChildren.push(cac('SellersItemIdentification', [cbc('ID', item.sellerItemId)]));
    }

    // Classified Tax Category
    itemChildren.push(cac('ClassifiedTaxCategory', [
        cbc('ID', item.vatCategoryCode),
        cbc('Percent', formatAmount(item.vatRate)),
        cac('TaxScheme', [cbc('ID', 'VAT')]),
    ]));

    children.push(cac('Item', itemChildren));

    // Price
    children.push(cac('Price', [
        cbc('PriceAmount', formatAmount(item.unitPrice), {currencyID: currencyCode}),
    ]));

    return cac('InvoiceLine', children);
}

/**
 * Build complete UBL 2.1 Invoice XML for XRechnung
 */
export function buildXRechnungXml(invoice: XRechnungInvoiceData): string {
    const parts: string[] = [];

    // XML declaration
    parts.push('<?xml version="1.0" encoding="UTF-8"?>');

    // Root element with namespaces
    parts.push(`<Invoice xmlns="${NAMESPACES.ubl}" xmlns:cac="${NAMESPACES.cac}" xmlns:cbc="${NAMESPACES.cbc}">`);

    // Document header
    parts.push(...buildDocumentHeader(invoice));

    // Seller party
    parts.push(buildAccountingSupplierParty(invoice.seller));

    // Buyer party
    parts.push(buildAccountingCustomerParty(invoice.buyer));

    // Delivery
    const delivery = buildDelivery(invoice.deliveryDate, invoice.deliveryLocation, invoice.deliveryAddress);
    if (delivery) {
        parts.push(delivery);
    }

    // Payment means
    parts.push(buildPaymentMeans(invoice.paymentMeans, invoice.invoiceNumber));

    // Payment terms
    const paymentTermsNote = invoice.paymentTerms?.note;
    const paymentTerms = buildPaymentTerms(paymentTermsNote);
    if (paymentTerms) {
        parts.push(paymentTerms);
    }

    // Tax total
    parts.push(buildTaxTotal(invoice.vatBreakdown, invoice.currencyCode));

    // Legal monetary total
    parts.push(buildLegalMonetaryTotal(invoice));

    // Invoice lines
    for (const item of invoice.lineItems) {
        parts.push(buildInvoiceLine(item, invoice.currencyCode));
    }

    // Close root element
    parts.push('</Invoice>');

    return parts.join('\n');
}
